import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
  computed,
  forwardRef,
  signal,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoPipe } from '@jsverse/transloco';
import {
  Subject,
  Subscription,
  debounceTime,
  distinctUntilChanged,
} from 'rxjs';
import { finalize } from 'rxjs/operators';
import { TooltipComponent } from '../custom-elements/tooltip/tooltip.component';
import {
  InfiniteDropdownFetchFn,
  InfiniteDropdownQuery,
} from './infinite-dropdown.models';

@Component({
  selector: 'app-infinite-dropdown',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    MatButtonModule,
    MatIconModule,
    TooltipComponent,
    TranslocoPipe,
  ],
  templateUrl: './infinite-dropdown.component.html',
  styleUrl: './infinite-dropdown.component.scss',
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InfiniteDropdownComponent),
      multi: true,
    },
  ],
})
export class InfiniteDropdownComponent<T = unknown>
  implements ControlValueAccessor, OnInit, AfterViewInit, OnDestroy, OnChanges
{
  @Input({ required: true }) fetchPage!: InfiniteDropdownFetchFn<T>;
  @Input() multiple = false;
  /**
   * When true with `multiple`, shows selected option labels over the value area (CSS overlay)
   * instead of mat-select-trigger — reads from `itemById` + optional prefetch when ids lack rows.
   */
  @Input() useMultiselectValueOverlay = false;
  /** When false, selection updates the display only; parent is notified on panel close or {@link commitValue}. */
  @Input() emitOnEveryChange = true;
  @Input() pageSize = 30;
  @Input() label: string | null = null;
  @Input() placeholderOption: string | null = null;
  /** When empty, falls back to `core.buttons.search`. */
  @Input() searchPlaceholder = '';
  @Input() loadingLabel = '…';
  @Input() loadingMoreLabel = '…';
  /** Rows merged for labels (e.g. current selection from parent). */
  @Input() prefetchedRows: T[] | null = null;
  @Input() labelAccessor: (item: T) => string = (item: T) =>
    String((item as Record<string, unknown>)['name'] ?? '');
  /**
   * When this returns a non-empty string, a non-selectable section header is rendered
   * immediately above that option (e.g. "Client contacts" vs "Order responsible").
   */
  @Input() sectionLabelAccessor: (item: T) => string | null = () => null;
  @Input() valueAccessor: (item: T) => string = (item: T) =>
    String((item as Record<string, unknown>)['id'] ?? '');
  @Input() compareWith = (a: unknown, b: unknown): boolean =>
    String(a) === String(b);
  @Input() required = false;
  @Input() clearable = true;
  /** When false, option rows do not show matTooltip (e.g. narrow filter menus). */
  @Input() optionMatTooltip = true;
  @Input() tooltip?: string;
  @Input() disableTooltip?: boolean = false;

  private _disabledInput = false;
  @Input()
  set disabled(value: boolean | null | undefined) {
    this._disabledInput = value ?? false;
    this.updateDisabledState();
  }
  get disabled(): boolean {
    return this._disabledInput;
  }

  private _disabledExternalInput = false;
  @Input()
  set disabledExternal(value: boolean | null | undefined) {
    this._disabledExternalInput = value ?? false;
    this.updateDisabledState();
  }
  get disabledExternal(): boolean {
    return this._disabledExternalInput;
  }

  @Input() readonly?: boolean | null;

  @ViewChild('matSelect', { read: MatSelect }) matSelect?: MatSelect;

  protected readonly loading = signal(false);
  protected readonly loadingMore = signal(false);
  protected readonly hasMore = signal(true);
  protected readonly rows = signal<T[]>([]);
  protected readonly itemById = signal(new Map<string, T>());
  /** Resolved id → label; survives itemById resets so overlay never flashes raw ids. */
  private readonly labelByIdCache = signal(new Map<string, string>());
  protected readonly value = signal<string | string[] | null>(null);
  protected readonly searchText = signal('');

  private readonly searchInput$ = new Subject<string>();
  private searchSub?: Subscription;
  private io: IntersectionObserver | null = null;
  private skip = 0;
  private currentSearch = '';
  private loadGeneration = 0;
  private prefetchSelectionLabelsGen = 0;
  private onChange: (v: string | string[] | null) => void = () => {
    /* noop */
  };
  private onTouched: () => void = () => {
    /* noop */
  };

  protected readonly _disabled = signal(false);
  protected readonly _readonly = signal(false);

  protected readonly displayRows = computed(() => {
    const byId = this.itemById();
    const raw = this.value();
    const selectedIds = this.multiple
      ? Array.isArray(raw)
        ? raw.map(String)
        : []
      : raw != null && raw !== ''
        ? [String(raw)]
        : [];
    const selectedSet = new Set(selectedIds);
    const selectedOrdered: T[] = [];
    for (const id of selectedIds) {
      const row = byId.get(id);
      if (row) {
        selectedOrdered.push(row);
      }
    }
    const rest: T[] = [];
    for (const row of this.rows()) {
      const id = this.valueAccessor(row);
      if (!selectedSet.has(id)) {
        rest.push(row);
      }
    }
    return [...selectedOrdered, ...rest];
  });

  protected readonly showClearButton = computed(() => {
    if (!this.clearable || this._disabled() || this._readonly()) {
      return false;
    }
    const v = this.value();
    if (this.multiple) {
      return Array.isArray(v) && v.length > 0;
    }
    if (v === null || v === undefined || v === '') {
      return false;
    }
    return true;
  });

  protected readonly overlayMultiselectLabelsText = computed(() => {
    if (!this.useMultiselectValueOverlay || !this.multiple) {
      return '';
    }
    const raw = this.value();
    const ids = Array.isArray(raw)
      ? raw.map(String).filter(id => id.trim() !== '')
      : [];
    if (!ids.length) {
      return '';
    }
    const byId = this.itemById();
    const cache = this.labelByIdCache();
    const parts: string[] = [];
    let pending = 0;
    for (const id of ids) {
      const row = byId.get(id);
      if (row) {
        parts.push(this.labelAccessor(row));
        continue;
      }
      const cached = cache.get(id);
      if (cached) {
        parts.push(cached);
        continue;
      }
      pending++;
    }
    if (parts.length === 0) {
      return '';
    }
    if (pending > 0) {
      parts.push('…');
    }
    return parts.join(', ');
  });

  ngOnInit(): void {
    this.updateDisabledState();
    this._readonly.set(this.readonly ?? false);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['prefetchedRows']) {
      this.mergePrefetchIntoMap();
      if (this.multiple && this.useMultiselectValueOverlay) {
        this.prefetchLabelsForCurrentSelection();
      }
    }
    if (changes['readonly']) {
      this._readonly.set(this.readonly ?? false);
    }
    if (
      changes['useMultiselectValueOverlay'] &&
      this.multiple &&
      this.useMultiselectValueOverlay
    ) {
      this.prefetchLabelsForCurrentSelection();
    }
  }

  ngAfterViewInit(): void {
    this.searchSub = this.searchInput$
      .pipe(debounceTime(250), distinctUntilChanged())
      .subscribe(term => {
        if (!this.matSelect?.panelOpen) {
          return;
        }
        this.currentSearch = term;
        this.beginNewSearchQuery();
      });
  }

  ngOnDestroy(): void {
    this.searchSub?.unsubscribe();
    this.teardownIntersectionObserver();
  }

  writeValue(v: string | string[] | null): void {
    if (this.multiple) {
      const arr = Array.isArray(v) ? v.map(String) : [];
      this.syncSelectedRowsIntoItemById(arr);
      this.value.set(arr);
    } else {
      const cleared = v != null && v !== '' ? String(v) : null;
      this.value.set(cleared);
      if (cleared === null) {
        this.searchText.set('');
      }
    }
    if (this.multiple && this.useMultiselectValueOverlay) {
      this.prefetchLabelsForCurrentSelection();
    }
    this.syncMatSelectValue();
  }

  registerOnChange(fn: (v: string | string[] | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  hasValue(): boolean {
    const v = this.value();
    if (this.multiple && Array.isArray(v)) {
      return v.length > 0;
    }
    return v != null && v !== '';
  }

  onSearchInput(raw: string): void {
    this.searchText.set(raw);
    this.searchInput$.next(raw.trim());
  }

  onPanelOpened(open: boolean): void {
    if (open) {
      this.searchText.set('');
      this.currentSearch = '';
      this.mergePrefetchIntoMap();
      this.beginNewSearchQuery();
      setTimeout(() => this.setupIntersectionObserver(), 0);
    } else {
      this.teardownIntersectionObserver();
      if (!this.emitOnEveryChange) {
        this.commitValue();
      }
    }
  }

  /** Current value (for parents that defer CVA updates until apply). */
  getValue(): string | string[] | null {
    return this.value();
  }

  /** Closes the option panel and commits deferred value when configured. */
  closePanel(): void {
    this.matSelect?.close();
  }

  commitValue(): void {
    this.onChange(this.value());
    this.onTouched();
  }

  onMatSelectionChange(v: string | string[] | null | undefined): void {
    if (this.multiple) {
      const arr = Array.isArray(v) ? v.map(String) : [];
      this.syncSelectedRowsIntoItemById(arr);
      this.value.set(arr);
      if (this.emitOnEveryChange) {
        this.onChange(arr);
        this.onTouched();
      }
      this.prefetchLabelsForCurrentSelection();
    } else {
      const out = v !== null && v !== undefined && v !== '' ? String(v) : null;
      if (out) {
        this.syncSelectedRowsIntoItemById([out]);
      }
      this.value.set(out);
      if (this.emitOnEveryChange) {
        this.onChange(out);
        this.onTouched();
      }
    }
  }

  clearSelection(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const cleared = this.multiple ? ([] as string[]) : null;
    this.value.set(cleared);
    if (!this.multiple) {
      this.searchText.set('');
    }
    this.syncMatSelectValue();
    this.onChange(cleared);
    this.onTouched();
  }

  /** Keeps mat-select trigger in sync when the parent clears the control programmatically. */
  private matSelectSyncQueued = false;
  private syncMatSelectValue(): void {
    if (this.matSelectSyncQueued) {
      return;
    }
    this.matSelectSyncQueued = true;
    queueMicrotask(() => {
      this.matSelectSyncQueued = false;
      const current = this.value();
      this.matSelect?.writeValue(
        this.multiple
          ? Array.isArray(current)
            ? current
            : []
          : (current ?? null)
      );
    });
  }

  private mergePrefetchIntoMap(): void {
    const pref = this.prefetchedRows;
    if (!pref?.length) {
      return;
    }
    const map = new Map(this.itemById());
    for (const row of pref) {
      map.set(this.valueAccessor(row), row);
    }
    this.setItemById(map);
    // Single-select trigger needs a matching option before it can show the label.
    this.syncMatSelectValue();
  }

  private setItemById(map: Map<string, T>): void {
    this.itemById.set(map);
    this.rememberLabels(map.values());
  }

  private rememberLabels(rows: Iterable<T>): void {
    let changed = false;
    const cache = new Map(this.labelByIdCache());
    for (const row of rows) {
      const id = this.valueAccessor(row);
      const label = this.labelAccessor(row);
      if (!id || !label) {
        continue;
      }
      if (cache.get(id) !== label) {
        cache.set(id, label);
        changed = true;
      }
    }
    if (changed) {
      this.labelByIdCache.set(cache);
    }
  }

  /** Resolves labels for current value ids when the map is incomplete (e.g. filter reopened). */
  private prefetchLabelsForCurrentSelection(): void {
    if (!this.multiple || !this.fetchPage) {
      return;
    }
    const raw = this.value();
    const ids = Array.isArray(raw)
      ? raw.map(String).filter(id => id.trim() !== '')
      : [];
    if (!ids.length) {
      return;
    }
    const byId = this.itemById();
    const missingIds = ids.filter(id => !byId.has(id));
    if (!missingIds.length) {
      return;
    }

    this.prefetchSelectionLabelsGen++;
    const gen = this.prefetchSelectionLabelsGen;
    const limit = Math.max(this.pageSize, missingIds.length);
    this.fetchPage({ skip: 0, limit, search: '' }).subscribe({
      next: page => {
        if (gen !== this.prefetchSelectionLabelsGen) {
          return;
        }
        const map = new Map(this.itemById());
        for (const row of page.rows ?? []) {
          map.set(this.valueAccessor(row), row);
        }
        this.setItemById(map);
      },
      error: () => {
        /* labels stay blank until rows load */
      },
    });
  }

  /** Keeps label rows for the current selection in `itemById`. */
  private syncSelectedRowsIntoItemById(selectedIds: string[]): void {
    if (!selectedIds.length) {
      return;
    }
    const map = new Map(this.itemById());
    const candidates = this.rows();
    for (const id of selectedIds) {
      if (map.has(id)) {
        continue;
      }
      for (const row of candidates) {
        if (this.valueAccessor(row) === id) {
          map.set(id, row);
          break;
        }
      }
    }
    this.setItemById(map);
  }

  private beginNewSearchQuery(): void {
    this.loadGeneration++;
    const gen = this.loadGeneration;
    this.skip = 0;
    this.rows.set([]);
    this.hasMore.set(true);
    this.loadNextPage(false, gen);
  }

  private loadNextPage(append: boolean, responseGen: number): void {
    if (append) {
      if (!this.hasMore() || this.loadingMore() || this.loading()) {
        return;
      }
    }
    const query: InfiniteDropdownQuery = {
      skip: this.skip,
      limit: this.pageSize,
      search: this.currentSearch,
    };
    if (append) {
      this.loadingMore.set(true);
    } else {
      this.loading.set(true);
    }
    this.fetchPage(query)
      .pipe(
        finalize(() => {
          this.loading.set(false);
          this.loadingMore.set(false);
        })
      )
      .subscribe({
        next: page => {
          if (responseGen !== this.loadGeneration) {
            return;
          }
          const batch = page.rows ?? [];
          const map = new Map(this.itemById());
          for (const row of batch) {
            map.set(this.valueAccessor(row), row);
          }
          this.setItemById(map);
          if (append) {
            const prev = this.rows();
            const seen = new Set(prev.map(r => this.valueAccessor(r)));
            const merged = [...prev];
            for (const row of batch) {
              const id = this.valueAccessor(row);
              if (!seen.has(id)) {
                seen.add(id);
                merged.push(row);
              }
            }
            this.rows.set(merged);
          } else {
            this.rows.set(batch);
          }
          const raw = this.value();
          if (this.multiple && Array.isArray(raw) && raw.length) {
            this.syncSelectedRowsIntoItemById(raw.map(String));
          }
          const count = page.count;
          this.skip += batch.length;
          if (page.hasMore !== undefined) {
            this.hasMore.set(page.hasMore);
          } else if (count != null) {
            this.hasMore.set(this.skip < count);
          } else {
            this.hasMore.set(batch.length >= this.pageSize);
          }
          setTimeout(() => this.setupIntersectionObserver(), 0);
        },
        error: () => {
          if (responseGen !== this.loadGeneration) {
            return;
          }
          this.hasMore.set(false);
        },
      });
  }

  private setupIntersectionObserver(): void {
    this.teardownIntersectionObserver();
    const root = this.matSelect?.panel?.nativeElement;
    const target = root?.querySelector('[data-infinite-dropdown-sentinel]');
    if (!root || !target) {
      return;
    }
    this.io = new IntersectionObserver(
      entries => {
        const hit = entries.some(e => e.isIntersecting);
        if (hit && this.hasMore() && !this.loading() && !this.loadingMore()) {
          const gen = this.loadGeneration;
          this.loadNextPage(true, gen);
        }
      },
      { root, rootMargin: '80px', threshold: 0 }
    );
    this.io.observe(target);
  }

  private teardownIntersectionObserver(): void {
    this.io?.disconnect();
    this.io = null;
  }

  private updateDisabledState(): void {
    this._disabled.set(this._disabledInput || this._disabledExternalInput);
  }
}
